'use client';

/**
 * API namespace `kyc`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L2760-L2985).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const kyc = {
  /** Get KYC status for current user.
   *
   *  R3-BATCH-6 (SPEC-047 4F-MED-008): `reviewStatus` previously typed as
   *  `string` — inconsistent with `api.kyc.getMyApplication` which already
   *  used the canonical `KycReviewStatus` union. The backend returns one
   *  of `'not_started' | 'pending_docs' | 'pending_review' | 'approved'
   *  | 'rejected'` (mirrors the Mongoose `KycReviewStatus` enum in
   *  `backend/src/models/kyc-application.model.ts` AND the shared
   *  `KycReviewStatus` in `packages/shared/src/types/kyc.ts:8`). Anti-§6
   *  (CM-19): single source of truth — the loose `string` type let callers
   *  pass invalid values without TypeScript catching the drift.
   *
   *  The `status: string` returned by `api.kyc.upload` + `api.kyc.start`
   *  + `api.kyc.submit` is left as `string` because those endpoints
   *  return the application's status AT CREATION TIME (always
   *  `'pending_docs'`) — the backend hasn't yet moved to a canonical
   *  shared return type for those endpoints. Narrowing them now would
   *  require coordinated backend changes (out of scope for BATCH-6).
   */
  getStatus(signal?: AbortSignal): Promise<{ kycLevel: number; reviewStatus: KycReviewStatus; rejectionReason: string | null; canWithdraw: boolean }> {
    return apiFetch<{ kycLevel: number; reviewStatus: KycReviewStatus; rejectionReason: string | null; canWithdraw: boolean }>('/kyc/status', { signal });
  },
  /** Get my KYC application — D-013 fix: added optional `signal` param.
   *  AUDIT3-SHARED-005 (SPEC-046 QA AUDIT R1 IA — BATCH-6): replace
   *  `application: any` + `status: string` with the canonical shared
   *  types `KycApplication | null` + `KycReviewStatus`. */
  getMyApplication(signal?: AbortSignal): Promise<{ application: KycApplication | null; status: KycReviewStatus }> {
    return apiFetch<{ application: KycApplication | null; status: KycReviewStatus }>('/kyc/my-application', { signal });
  },
  /**
   * Sprint 138 (PR-D geo-fields): PUT /api/kyc/geo-fields — Update
   * state/city/countryOfResidence without re-submitting FATF form
   * or re-verifying KYC (INV-KYCGEO-06 — "Editable sin re-verificación").
   */
  async updateGeoFields(data: {
    state: string;
    city: string;
    countryOfResidence: string;
  }): Promise<{ state: string; city: string; countryOfResidence: string }> {
    return apiFetch('/kyc/geo-fields', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  /** Start KYC application with base64 docs (legacy).
   *
   *  R3-BATCH-6 (SPEC-047 4F-MED-007): `documentType` previously typed as
   *  `string` — now narrowed to the canonical `KycDocumentType` union
   *  (`'passport' | 'national_id' | 'drivers_license'`). Mirrors the
   *  Mongoose enum in `backend/src/models/kyc-application.model.ts:12`
   *  AND the Zod enum in `packages/shared/src/schemas/kyc.ts:116`.
   *  Anti-§6: closed union — prevents callers from passing values the
   *  backend would reject with Mongoose validation error (400).
   *
   *  The return shape (`{ applicationId, status, createdAt }`) is kept
   *  loose-typed because the backend hasn't migrated to a shared DTO
   *  for the start response — `status: string` is always `'pending_docs'`
   *  at creation time (the application has just been created with docs
   *  uploaded but not yet submitted for review). */
  start(data: {
    country: string;
    documentType: KycDocumentType;
    documentNumber: string;
    documentFrontBase64: string;
    documentBackBase64?: string;
    selfieBase64: string;
    proofOfAddressBase64?: string;
  }): Promise<{ applicationId: string; status: string; createdAt: string }> {
    return apiFetch('/kyc/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  /** Upload KYC documents via multipart/form-data */
  upload(formData: FormData): Promise<{ applicationId: string; status: string; createdAt: string }> {
    // FE-004 fix: Previously used raw `fetch()` with manual Authorization
    // header and no 401 refresh handling. Now delegates to
    // `apiFetchFormData()` which mirrors `apiFetch`'s 401 → tryRefresh →
    // retry logic, but omits `Content-Type: application/json` so the
    // browser can set the correct multipart boundary for FormData.
    //
    // The backend POST /kyc/upload returns the standard
    // `{ data: { applicationId, status, createdAt } }` envelope
    // (introduced by SPEC-037 commit fefecb1). Previously this method
    // returned the raw `body` (still wrapped in `{ data: ... }`) while the
    // TS type claimed it was unwrapped — a type lie that would surface as a
    // runtime undefined the moment any caller tried to read applicationId.
    //
    // `apiFetchFormData` → `handleResponse` already unwraps the envelope,
    // so the returned value matches the declared return type.
    return apiFetchFormData('/kyc/upload', {
      method: 'POST',
      body: formData,
    });
  },
  /** Submit application for review */
  submit(applicationId: string): Promise<{ applicationId: string; status: string }> {
    return apiFetch('/kyc/submit', {
      method: 'POST',
      body: JSON.stringify({ applicationId }),
    });
  },
  /**
   * Submit KYC application with pre-uploaded documentIds (progressive upload flow).
   *
   * Bug fix (Founder QA 2026-09-16): pre-fix, `api.kyc.upload` sent all
   * 4 files in a single FormData — 30s timeout aborted uploads > 4MB.
   * Post-fix, the frontend uses `api.kyc.uploadDocument` (per file, via
   * XHR with progress) to get a `documentId` per file, then calls this
   * method to submit the application with just the documentIds (small
   * JSON body, no files).
   *
   * Backend: POST /api/kyc/applications — reads the files from disk via
   * the documentIds (stored in Redis with 1h TTL), creates the
   * KycApplication, auto-submitForReview.
   *
   * §6 Anti-Invención: same backend flow as /kyc/upload (legacy) —
   * validateDocument + storeFile + kycService.createApplication +
   * submitForReview. Only the transport changes (JSON refs vs multipart).
   */
  submitApplication(payload: {
    country: string;
    documentType: string;
    documentNumber: string;
    documentIds: {
      front: string;
      back?: string;
      selfie: string;
      proofOfAddress?: string;
    };
  }): Promise<{ applicationId: string; status: string; createdAt: string }> {
    return apiFetch('/kyc/applications', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  /**
   * PR-K-004-endpoint (Sprint 131-2 paso 2/4 — Bug K-004 P0 feature faltante)
   *
   * POST /api/kyc/complete-profile — Step 1 of the 3-step KYC wizard:
   * register FATF (Customer Due Diligence) data BEFORE uploading identity
   * documents. Returns `{ applicationId, fatfCompletedAt }` — the
   * `fatfCompletedAt` timestamp gates Step 2 (document upload).
   *
   * Anti-§6 (CM-19/CM-15): the `data` parameter is typed as `KycFatfInput`
   * — the canonical Zod-inferred type from the shared package
   * (`packages/shared/src/schemas/kyc-fatf.ts`). Frontend + backend share
   * the same canonical validation rules — no inline type that drifts.
   * The schema enforces:
   *   - firstName/lastName: 2-50 chars, Unicode-aware regex
   *   - dateOfBirth: strict YYYY-MM-DD + age >= 18 (FATF requirement)
   *   - nationality/countryOfResidence: ISO 3166-1 alpha-2 (2 uppercase)
   *   - address: 10-200 chars (free-text — international addresses OK)
   *   - sourceOfFunds: enum (salary/savings/investment/inheritance/gift/
   *     business/other — FATF AML taxonomy copied verbatim from GamesP2P)
   *   - sourceOfFundsDetails: optional, max 500 chars
   *
   * The caller can also use `kycFatfSchema.safeParse(data)` for client-side
   * pre-validation before calling this method (recommended UX — surfaces
   * field-level errors before the network roundtrip).
   *
   * §6 Anti-Invención: NO new KYC levels, fees, or rules invented. The
   * endpoint + schema + types are copied faithfully from GamesP2P
   * `kyc.js:982-1326` per SPEC §6. 2FA NOT required (per SPEC §9.6 — this
   * is data registration, not a financial operation).
   *
   * Spec ref: docs/SPEC-QA-AUDIT-SPRINT-131-2-KYC.md §3 (K-004) + §4 + §6
   */
  completeKycProfile(data: KycFatfInput): Promise<{ applicationId: string; fatfCompletedAt: string }> {
    return apiFetch('/kyc/complete-profile', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  /**
   * PR-K-004-step3 (Sprint 131-2 — KYC Level 3 proof of funds)
   *
   * POST /api/kyc/proof-of-funds — Step 3 of the 3-step KYC wizard:
   * upload the proof of funds document (bank statement / tax return /
   * accountant letter) required for KYC Level 3 (sin tope de retiro per
   * SPEC-NEGOCIO §4.5). Optional — the user can SKIP Step 3 entirely; the
   * wizard's "Saltar y enviar solo KYC Level 1/2" button calls
   * `setKycStatus('pending_review')` directly (no API call needed —
   * Step 2's submitForReview already set the status to pending_review).
   *
   * Sends a multipart/form-data with a single `proofOfFunds` File field.
   * Uses `apiFetchFormData` (the canonical multipart helper, same as
   * `api.kyc.upload`) for the 401 → tryRefresh retry path on FormData
   * bodies. Anti-§6: the backend endpoint mirrors the existing `/kyc/
   * complete-profile` + `/kyc/upload` auth posture (requireAuth, NO 2FA
   * per SPEC §9.6 — data registration, not a financial operation).
   *
   * §6 Anti-Invención (Founder directive 2026-09-18): H-K-1 bonos KYC
   * RECHAZADO por Founder — NO apply to Profileia. This method ONLY uploads
   * the document; the admin decides the KYC level (0/1/2/3 immutable per
   * §4.5). NO invented fees, thresholds, or anti-MC rules.
   *
   * Spec ref:
   *   - docs/SPEC-QA-AUDIT-SPRINT-131-2-KYC.md §3 (K-004) + §4 (wizard) + §6
   *   - SPEC-NEGOCIO §4.5 (KYC) + §9.5 (PII cifrada) + §9.6 (2FA scope)
   */
  submitKycProofOfFunds(data: { proofOfFunds: File }): Promise<{ applicationId: string }> {
    const formData = new FormData();
    formData.append('proofOfFunds', data.proofOfFunds);
    // apiFetchFormData mirrors apiFetch's 401 → tryRefresh → retry flow
    // but omits `Content-Type: application/json` so the browser sets the
    // multipart boundary. The backend returns `{ data: { applicationId } }`
    // (the standard envelope) which handleResponse unwraps to the inner
    // `{ applicationId }` shape declared as the return type.
    return apiFetchFormData('/kyc/proof-of-funds', {
      method: 'POST',
      body: formData,
    });
  },
};
